import { Inventory, Question, Role } from "../types";
import { getInventoryIdsRequiringTask, hasAnyPlaceTaskTag, hasTaskByInventoryTagRaw, parsePlaceTags } from "./placeTags";

const LOG_PREFIX = "[inventory-tag-task]";

export function logInventoryTagTask(message: string, data?: unknown): void {
  if (data !== undefined) {
    console.log(LOG_PREFIX, message, data);
  } else {
    console.log(LOG_PREFIX, message);
  }
}

export function warnInventoryTagTask(message: string, data?: unknown): void {
  if (data !== undefined) {
    console.warn(LOG_PREFIX, message, data);
  } else {
    console.warn(LOG_PREFIX, message);
  }
}

export interface InventorySubmissionResult {
  inventoryId: string;
  inventoryName: string;
  checkIds: string[];
  questions: Question[];
}

type AnswerLike = Record<string, unknown>;

export function findInventoryMeta(
  inventoryList: Inventory[],
  inventoryId: string
): Inventory | undefined {
  return inventoryList.find((item) => item.id === inventoryId);
}

export function resolveGuestInitiatorId(
  inventoryRoleId?: string,
  roles?: Role[]
): string | undefined {
  const anonymousRole = roles?.find((r) => /nepřihlášen/i.test(r.name));
  return anonymousRole?.id || inventoryRoleId;
}

export function pickCheckIdByHighestPriority(
  questions: Question[],
  checkIds: string[],
  getAnswer: (question: Question) => AnswerLike
): string | undefined {
  let bestCheckId: string | undefined;
  let bestPriority = Number.NEGATIVE_INFINITY;

  questions.forEach((question, index) => {
    const answer = getAnswer(question);
    if (answer?.skip_check) return;

    const checkId = checkIds[index];
    if (!checkId) return;

    const priority = question.priority ?? 0;
    if (bestCheckId == null || priority > bestPriority) {
      bestPriority = priority;
      bestCheckId = checkId;
    }
  });

  return bestCheckId ?? checkIds.find(Boolean);
}

export function buildTaskDescriptionFromAnswers(
  questions: Question[],
  getAnswer: (question: Question) => AnswerLike,
  formatAnswerValue: (question: Question, answer: AnswerLike) => string
): string {
  const lines: string[] = [];

  for (const question of questions) {
    const answer = getAnswer(question);
    if (answer?.skip_check) continue;
    const answerText = formatAnswerValue(question, answer || {});
    lines.push(`${question.question_text} - ${answerText}`);
  }

  return lines.join("\n");
}

export function shouldCreateInventoryTagTask(
  inventoryList: Inventory[],
  inventoryId: string,
  options?: { log?: boolean }
): boolean {
  const log = options?.log ?? false;

  if (!inventoryId || inventoryList.length === 0) {
    if (log) {
      warnInventoryTagTask("Tag task přeskočen – chybí inventoryId nebo prázdný seznam inventáře", {
        inventoryId,
        inventoryCount: inventoryList.length,
      });
    }
    return false;
  }

  const requiredIds = getInventoryIdsRequiringTask(inventoryList);

  if (requiredIds.has(inventoryId)) {
    if (log) {
      logInventoryTagTask("Tag task – přesná shoda inventáře v tagu", {
        inventoryId,
        requiredIds: Array.from(requiredIds),
      });
    }
    return true;
  }

  for (const item of inventoryList) {
    if (hasTaskByInventoryTagRaw(item.place_tags, inventoryId)) {
      if (log) {
        logInventoryTagTask("Tag task nalezen v place_tags položky", {
          inventoryId,
          inventoryItemId: item.id,
          inventoryItemName: item.name,
          place_tags: item.place_tags,
          parsedTags: parsePlaceTags(item.place_tags),
        });
      }
      return true;
    }
  }

  if (hasAnyPlaceTaskTag(inventoryList)) {
    if (log) {
      logInventoryTagTask("Tag task – místo má task_by_* tag, odeslán libovolný inventář", {
        submittedInventoryId: inventoryId,
        requiredIds: Array.from(requiredIds),
      });
    }
    return true;
  }

  if (log) {
    warnInventoryTagTask("Tag task nenalezen – místo nemá žádný task_by_* tag", {
      inventoryId,
      requiredIds: Array.from(requiredIds),
      placeTagsSample: inventoryList.slice(0, 2).map((i) => ({
        id: i.id,
        name: i.name,
        place_tags: i.place_tags,
        parsed: parsePlaceTags(i.place_tags),
      })),
    });
  }

  return false;
}
